import styled from '@emotion/styled';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import { typographyVariantStyles } from '../../typography-variants';

const TRANSIENT_PROPS = new Set(['$current']);

const itemBaseStyles = (
  theme: { colors: { text: { primary: string }; brand: { solid: string } } },
  current?: boolean,
) =>
  ({
    ...typographyVariantStyles.body2,
    fontWeight: current ? 500 : 400,
    color: current ? theme.colors.text.primary : alpha(theme.colors.text.primary, 0.2),
    textDecoration: 'none',
    whiteSpace: 'nowrap' as const,
    transition: 'color 0.15s ease',
    outline: 'none',
    cursor: current ? 'default' : 'pointer',

    ...(!current && {
      '&:hover': {
        color: alpha(theme.colors.text.primary, 0.6),
      },
      '&:focus-visible': {
        color: theme.colors.text.primary,
        borderRadius: 2,
        boxShadow: `0 0 0 2px ${alpha(theme.colors.brand.solid, 0.4)}`,
      },
    }),
  }) as const;

export const StyledNav = styled('nav')({
  display: 'inline-flex',
});

export const StyledList = styled('ol')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 36,
  padding: '0 12px',
  margin: 0,
  listStyle: 'none',
});

export const StyledListItem = styled('li')({
  display: 'inline-flex',
  alignItems: 'center',
});

export const StyledItemSpan = styled('span', {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop as string),
})<{ $current?: boolean }>(({ theme, $current }) => itemBaseStyles(theme, $current));

export const StyledItemLink = styled(Link, {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop as string),
})<{ $current?: boolean }>(({ theme, $current }) => itemBaseStyles(theme, $current));

export const StyledItemButton = styled('button', {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop as string),
})<{ $current?: boolean }>(({ theme, $current }) => ({
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  ...itemBaseStyles(theme, $current),
}));

export const StyledSeparator = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: alpha(theme.colors.text.primary, 0.2),
  pointerEvents: 'none',
}));

export const StyledEllipsisButton = styled('button')(({ theme }) => ({
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,

  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,

  width: 16,
  height: 16,
  color: alpha(theme.colors.text.primary, 0.2),
  cursor: 'pointer',
  outline: 'none',
  transition: 'color 0.15s ease',
  borderRadius: 2,

  '&:hover': {
    color: alpha(theme.colors.text.primary, 0.6),
  },

  '&:focus-visible': {
    color: theme.colors.text.primary,
    boxShadow: `0 0 0 2px ${alpha(theme.colors.brand.solid, 0.4)}`,
  },
}));
